import {useEffect} from 'react';
import _ from 'lodash';

import {useNavigate, useLocation} from 'react-router-dom';
import {initReducerContext} from '@someok/simple-reducer-store';
import {getCachedProfile, getTokens} from '@/util/TokenStore';

import {doProfile} from '@/api/auth';

import authActions from './actions';

export const GLOBAL_ADMIN_READ = 'global:admin:r';
export const GLOBAL_ADMIN_WRITE = 'global:admin:w';

/**
 * 判断用户是否存在给定权限。
 *
 * @param auths 允许的权限
 * @param useGlobalRW 允许读取超级权限中的读或写权限，如果不提供，则不读取超级权限
 * @return {boolean}
 */
function hasAuthorities(auths, useGlobalRW) {
    // console.log('auths, readOrWrite', auths, useGlobalRW);
    if (_.isEmpty(auths)) {
        throw new Error('[auths] 参数不能为空');
    }

    const userAuths = this.authorities ?? [];

    for (const auth of auths) {
        if (userAuths.includes(auth)) {
            return true;
        }
    }

    if (useGlobalRW === 'r') {
        return userAuths.includes(GLOBAL_ADMIN_READ);
    }
    if (useGlobalRW === 'w') {
        return userAuths.includes(GLOBAL_ADMIN_WRITE);
    }

    return false;
}

/**
 * 检测当前人员是否有系统管理权限
 */
function hasGlobalAuths() {
    const userAuths = this.authorities ?? [];

    if (_.isEmpty(userAuths)) {
        return false;
    }

    return !!userAuths.find(auth => auth.startsWith('global:'));
}

const initialState = {
    isLogin: false,
    user: undefined,
    authorities: undefined,

    hasAuthorities,
    hasGlobalAuths,
};

const [useAuthStore, AuthStoreProvider, AuthContext] = initReducerContext(
    initialState,
    authActions
);

export {useAuthStore, AuthStoreProvider, AuthContext};

export const IGNORE_ROUTES = ['/login', '/logout','/article','/sso','/dev-login'];

/**
 * 给定页面是否可公开访问。
 *
 * @param pathname
 * @return {boolean}
 */
function isPublicRoute(pathname) {
    for (let ignoreroute of IGNORE_ROUTES) {
        if (pathname.startsWith(ignoreroute)) {
            return true;
        }
    }
    return false;
}

/**
 * 检查登录信息是否存在不存在则跳转到登录页。
 *
 * @return {null}
 * @constructor
 */
export function AuthChecker() {
    const [authStore, dispatch] = useAuthStore();
    const navigate = useNavigate();
    let location = useLocation();
    const {isLogin} = authStore;

    useEffect(() => {
        if (isPublicRoute(location.pathname)) {
            console.log('ignore check auth...');
            return;
        }
        if (!isLogin) {
            const tokens = getTokens();
            if (tokens) {
                // 优先使用本地缓存的用户信息，刷新页面后立即可用，避免短暂缺失
                const cachedProfile = getCachedProfile();
                if (cachedProfile && cachedProfile.user) {
                    dispatch(['loginSuccess', {tokens, ...cachedProfile}]);
                }
                // 同时请求最新用户信息刷新缓存
                doProfile().then(json => {
                    if (json && json.data) {
                        // console.log(data);
                        dispatch(['loginSuccess', json.data]);
                    }
                });
            } else {
                navigate('/login', {replace: true});
            }
        }
    }, [authStore.user, dispatch, navigate, isLogin, location.pathname]);

    return null;
}
